import _ from 'lodash';
import React, { useEffect } from 'react';

import { MOCK_PORT } from 'scbk-services/config/constants';
import { Envs } from 'scbk-services/config/enums/envs';
import { Markets } from 'scbk-services/config/enums/markets';
import { useConfigStore } from 'scbk-services/config/stores/configStore';
import { getUrlParameter } from 'scbk-services/utils/urlParameter';

export const useEnvContext = () => {
    const setStoreMarket = useConfigStore((state) => state.setMarket);
    const getMarket = useConfigStore((state) => state.getMarket);

    const setStoreEnv = useConfigStore((state) => state.setEnv);
    const getEnv = useConfigStore((state) => state.getEnv);

    const getPreDefinedMarket = (metaEnv: ImportMetaEnv) => {
        // First, get pre-defined market from url params
        const marketParam = getUrlParameter('market');
        if (marketParam && Object.values(Markets).includes(marketParam as Markets)) {
            return marketParam as Markets;
        }

        // Finally, fall back to metaEnv
        return metaEnv.VITE_MARKET as Markets;
    };

    const getPreDefinedEnv = (metaEnv: ImportMetaEnv) => {
        // First, get pre-defined env from url params
        const envParam = getUrlParameter('env');
        if (envParam && Object.values(Envs).includes(envParam as Envs)) {
            return envParam as Envs;
        }

        // Secondly, If no valid env parameter, check for MOCK_PORT
        if (window.location.href.includes(MOCK_PORT)) {
            return Envs.MOCK;
        }

        // Finally, fall back to metaEnv
        return metaEnv.VITE_ENVIRONMENT as Envs;
    };

    const setMarket = (market: Markets) => {
        const currentMarket = getMarket();
        let finalMarket = currentMarket;
        if (!_.isEmpty(market) && Object.values(Markets).includes(market as Markets)) {
            finalMarket = market;
        }
        if (currentMarket !== finalMarket) {
            setStoreMarket(finalMarket);
        }
    };

    const setEnv = (env: Envs) => {
        const currentEnv = getEnv();
        let finalEnv = currentEnv;
        if (!_.isEmpty(env) && Object.values(Envs).includes(env as Envs)) {
            finalEnv = env;
        }
        if (currentEnv !== finalEnv) {
            setStoreEnv(finalEnv);
        }
    };

    const useSubscribeEnvContext = (
        callback: (changes: { market?: Markets; env?: Envs }) => void,
        dependencies: React.DependencyList = [],
    ) => {
        useEffect(() => {
            const unsubscribe = useConfigStore.subscribe((state, prevState) => {
                const changes: {
                    market?: Markets;
                    env?: Envs;
                } = {};

                if (state.currentMarket !== prevState.currentMarket) {
                    changes.market = state.currentMarket;
                }

                if (state.currentEnv !== prevState.currentEnv) {
                    changes.env = state.currentEnv;
                }

                if (Object.keys(changes).length > 0) {
                    callback(changes);
                }
            });
            return () => unsubscribe();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [...dependencies]);
    };

    return {
        getMarket,
        setMarket,
        getEnv,
        setEnv,
        getPreDefinedMarket,
        getPreDefinedEnv,
        useSubscribeEnvContext,
    };
};

export default useEnvContext;
